import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import { useCRMData } from '../context/CRMDataContext';
import { formatCurrency } from '../utils/helpers';
import ActivityForm from '../components/forms/ActivityForm';
import BaseModal from '../components/modals/BaseModal';
import CompanyForm from '../components/forms/CompanyForm';
import DealForm from '../components/forms/DealForm';
import CreateDocumentModal from '../components/modals/CreateDocumentModal';

const COMPANY_DETAIL_TAB_MAP = {
  timeline: 'Tidslinje',
  activities: 'Aktiviteter',
  notes: 'Notater',
  files: 'Filer',
  documents: 'Dokumenter',
  email: 'E-post'
};

const formatNorwegianDate = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('nb-NO', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  });
};

const isHiddenStatus = status => ['Slettet', 'Deleted', 'Arkivert'].includes(status);
const getRecordId = record => record?.activity_id || record?.id;

const isNoteActivity = activity => {
  const type = String(activity?.type || '').toLowerCase();
  const activityType = String(activity?.activityType || activity?.activity_type || '').toLowerCase();
  return type === 'notat' || type === 'note' || activityType === 'note' || activityType === 'notat';
};

const DocumentCard = React.memo(({
  id,
  name,
  title,
  description,
  type,
  status,
  date,
  created_at,
  onOpen
}) => {
  const record = {
    id,
    activity_id: id,
    name,
    title,
    description,
    type,
    status,
    date,
    created_at
  };

  return (
    <div
      onClick={() => onOpen(record)}
      className="p-4 border border-slate-200 rounded-xl flex justify-between items-center bg-white hover:border-blue-300 transition-colors cursor-pointer"
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-slate-50 border border-slate-100 rounded-lg flex items-center justify-center text-slate-500">
          <SafeIcon icon="📄" className="w-5 h-5" />
        </div>
        <div>
          <h4 className="font-bold text-slate-900 text-sm">{record.name || record.title || 'Dokument'}</h4>
          {record.description && <p className="text-xs text-slate-600">{record.description}</p>}
          <p className="text-xs text-slate-500">
            {record.type || 'Dokument'} • {formatNorwegianDate(record.date || record.created_at || Date.now())}
          </p>
        </div>
      </div>
      <span className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700">
        {record.status}
      </span>
    </div>
  );
});

const CompanyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    deals,
    sentContracts,
    currentUser,
    updateCompany
  } = useCRM();
  const {
    companies,
    contacts,
    deals: dataDeals,
    activities,
    addActivity,
    updateActivity
  } = useCRMData();

  const [activeTab, setActiveTab] = useState('Dokumenter');
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showEmailLogForm, setShowEmailLogForm] = useState(false);
  const [showFileForm, setShowFileForm] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isEditingDocument, setIsEditingDocument] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingFile, setIsEditingFile] = useState(false);
  const [documentForm, setDocumentForm] = useState({ title: '', description: '' });
  const [emailForm, setEmailForm] = useState({ title: '', description: '' });
  const [emailEditForm, setEmailEditForm] = useState({ title: '', description: '' });
  const [fileForm, setFileForm] = useState({ title: '', description: '' });
  const [fileEditForm, setFileEditForm] = useState({ title: '', description: '' });
  const [emailFormError, setEmailFormError] = useState('');
  const [emailEditError, setEmailEditError] = useState('');
  const [fileFormError, setFileFormError] = useState('');
  const [fileEditError, setFileEditError] = useState('');
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [isSavingEmailEdit, setIsSavingEmailEdit] = useState(false);
  const [isSavingFile, setIsSavingFile] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTagInput, setNewTagInput] = useState('');
  const [tagColor, setTagColor] = useState('blue');

  const tagColors = [
    { name: 'Blå', value: 'blue', bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
    { name: 'Grønn', value: 'green', bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200' },
    { name: 'Rød', value: 'red', bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200' },
    { name: 'Gul', value: 'yellow', bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
    { name: 'Lilla', value: 'purple', bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
    { name: 'Grå', value: 'gray', bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-200' }
  ];

  const company = companies.find(item => (
    String(item.company_id || item.id) === String(id)
  ));

  const companyIds = [
    company?.company_id,
    company?.id,
    id
  ].filter(Boolean).map(String);

  const companyContacts = contacts.filter(contact => {
    const contactCompanyIds = [
      contact.company_id,
      contact.companyId
    ].filter(Boolean).map(String);

    return contactCompanyIds.some(contactCompanyId => companyIds.includes(contactCompanyId));
  });

  const selectedDealId = searchParams.get('deal_id') || searchParams.get('dealId');
  const normalizedSelectedDealId = selectedDealId ? String(selectedDealId) : '';
  const selectedTab = searchParams.get('tab');
  const selectedDealFromData = Array.isArray(dataDeals)
    ? dataDeals.find(deal => String(deal.deal_id || deal.id) === normalizedSelectedDealId)
    : null;
  const selectedDealFromCRM = Array.isArray(deals)
    ? deals.find(deal => String(deal.deal_id || deal.id) === normalizedSelectedDealId)
    : null;
  const selectedDeal = selectedDealFromData || selectedDealFromCRM || null;
  const isDealContext = Boolean(normalizedSelectedDealId);

  const companyActivities = activities
    .filter(activity => {
      const dealId = activity.deal_id || activity.dealId;
      if (selectedDealId) {
        return String(dealId || '') === String(selectedDealId || '');
      }
      return String(activity.company_id || activity.companyId || '') === String(id || '');
    })
    .sort((a, b) => new Date(b.date || b.created_at || 0) - new Date(a.date || a.created_at || 0));

  const companyNotes = companyActivities.filter(isNoteActivity);
  const visibleCompanyActivities = companyActivities.filter(activity => !isNoteActivity(activity));

  const companyDeals = deals.filter(deal => deal.company_id === id);
  const companyDocuments = sentContracts?.filter(document => {
    if (isHiddenStatus(document.status)) return false;
    const documentDealId = document.deal_id || document.dealId;
    if (selectedDealId) return documentDealId === selectedDealId;
    return document.company_id === id || document.companyId === id;
  }) || [];

  const emailActivities = companyActivities.filter(activity => (
    activity.type === 'E-post' && !isHiddenStatus(activity.status)
  ));

  const fileActivities = activities
    .filter(activity => {
      if (activity.type !== 'Fil' || isHiddenStatus(activity.status)) return false;
      if (selectedDealId) return (activity.deal_id || activity.dealId) === selectedDealId;
      return activity.company_id === id || activity.companyId === id;
    })
    .sort((a, b) => new Date(b.date || b.created_at || 0) - new Date(a.date || a.created_at || 0));

  const activityDocuments = activities
    .filter(activity => {
      if (activity.type !== 'Dokument' || isHiddenStatus(activity.status)) return false;
      if (selectedDealId) return activity.deal_id === selectedDealId;
      return activity.company_id === id || activity.companyId === id;
    })
    .map(activity => ({
      id: activity.activity_id,
      activity_id: activity.activity_id,
      name: activity.title,
      title: activity.title,
      type: activity.type,
      status: activity.status,
      description: activity.description,
      date: activity.date,
      end_date: activity.end_date,
      created_at: activity.created_at,
      company_id: activity.company_id,
      deal_id: activity.deal_id,
      user_id: activity.user_id
    }));

  const documentLookupRef = useRef([]);
  documentLookupRef.current = activityDocuments.length ? activityDocuments : companyDocuments;

  const handleOpenDocument = useCallback(document => {
    const key = getRecordId(document);
    const resolved = documentLookupRef.current.find(item => getRecordId(item) === key);
    setSelectedDocument(resolved || document || null);
    setIsEditingDocument(false);
  }, []);

  const handleOpenEmail = useCallback(email => {
    setSelectedEmail(email);
    setIsEditingEmail(false);
    setEmailEditError('');
  }, []);

  const handleOpenFile = useCallback(file => {
    setSelectedFile(file);
    setIsEditingFile(false);
    setFileEditError('');
  }, []);

  useEffect(() => {
    const mappedTab = COMPANY_DETAIL_TAB_MAP[selectedTab];
    if (mappedTab) setActiveTab(mappedTab);
  }, [selectedTab]);

  useEffect(() => {
    if (!company && companies.length > 0) navigate('/companies');
  }, [company, companies, navigate]);

  if (!company) return null;

  const totalDealSize = companyDeals.reduce((sum, deal) => sum + Number(deal.value || 0), 0);
  const avgProbability = companyDeals.length
    ? Math.round(companyDeals.reduce((sum, deal) => sum + (deal.probability || 0), 0) / companyDeals.length)
    : 0;

  const handleSaveNote = () => {
    if (!newNote.trim()) return;
    addActivity({
      type: 'Notat',
      title: 'Notat lagt til',
      description: newNote,
      company_id: company.company_id,
      deal_id: selectedDealId || null,
      status: 'Gjennomført',
      date: new Date().toISOString(),
      user_id: currentUser?.user_id
    });
    setNewNote('');
  };

  const handleLogEmail = async () => {
    const title = emailForm.title.trim();
    if (!title) {
      setEmailFormError('Emne må fylles ut.');
      return;
    }

    setIsSavingEmail(true);
    const result = await addActivity({
      title,
      description: emailForm.description,
      type: 'E-post',
      status: 'Gjennomført',
      date: new Date().toISOString(),
      company_id: company.company_id,
      deal_id: selectedDealId || null
    });
    setIsSavingEmail(false);

    if (!result?.success) {
      setEmailFormError(result?.error?.message || 'Kunne ikke loggføre e-posten.');
      return;
    }

    setEmailForm({ title: '', description: '' });
    setEmailFormError('');
    setShowEmailLogForm(false);
  };

  const handleRegisterFile = async () => {
    const title = fileForm.title.trim();
    if (!title) {
      setFileFormError('Filnavn må fylles ut.');
      return;
    }

    setIsSavingFile(true);
    setFileFormError('');
    const result = await addActivity({
      title,
      description: fileForm.description,
      type: 'Fil',
      status: 'Gjennomført',
      date: new Date().toISOString(),
      company_id: company.company_id,
      deal_id: selectedDealId || null
    });
    setIsSavingFile(false);

    if (!result?.success) {
      setFileFormError(result?.error?.message || 'Kunne ikke registrere filmetadata.');
      return;
    }

    setFileForm({ title: '', description: '' });
    setShowFileForm(false);
  };

  const handleStartEditEmail = () => {
    if (!selectedEmail) return;
    setEmailEditForm({
      title: selectedEmail.title || '',
      description: selectedEmail.description || ''
    });
    setIsEditingEmail(true);
  };

  const handleSaveEmail = async () => {
    if (!selectedEmail) return;
    const title = emailEditForm.title.trim();
    if (!title) {
      setEmailEditError('Emne må fylles ut.');
      return;
    }

    const activityId = getRecordId(selectedEmail);
    const existing = activities.find(activity => getRecordId(activity) === activityId);
    if (!activityId || !existing) {
      setEmailEditError('Fant ikke e-postloggen som skulle oppdateres.');
      return;
    }

    setIsSavingEmailEdit(true);
    const result = await updateActivity(activityId, {
      title,
      description: emailEditForm.description,
      type: 'E-post',
      status: 'Gjennomført',
      company_id: existing.company_id || selectedEmail.company_id || company.company_id,
      deal_id: existing.deal_id || selectedEmail.deal_id || selectedDealId || null,
      date: existing.date || selectedEmail.date || selectedEmail.created_at || new Date().toISOString(),
      end_date: existing.end_date || selectedEmail.end_date || null,
      user_id: existing.user_id || selectedEmail.user_id || currentUser?.user_id || null
    });
    setIsSavingEmailEdit(false);

    if (!result?.success) {
      setEmailEditError(result?.error?.message || 'Kunne ikke oppdatere e-postloggen.');
      return;
    }

    setSelectedEmail({
      ...selectedEmail,
      ...result.data,
      id: result.data.activity_id || result.data.id || activityId,
      activity_id: result.data.activity_id || result.data.id || activityId,
      title,
      description: emailEditForm.description,
      type: 'E-post',
      status: 'Gjennomført'
    });
    setIsEditingEmail(false);
  };

  const handleDeleteEmail = async () => {
    if (!selectedEmail) return;
    if (!window.confirm('Er du sikker på at du vil slette denne e-postloggen?')) return;

    const activityId = getRecordId(selectedEmail);
    const existing = activities.find(activity => getRecordId(activity) === activityId);
    if (!activityId || !existing) return;

    setIsSavingEmailEdit(true);
    const result = await updateActivity(activityId, {
      title: existing.title || selectedEmail.title || 'E-post',
      description: existing.description || selectedEmail.description || '',
      type: 'E-post',
      status: 'Slettet',
      company_id: existing.company_id || selectedEmail.company_id || company.company_id,
      deal_id: existing.deal_id || selectedEmail.deal_id || selectedDealId || null,
      date: existing.date || selectedEmail.date || selectedEmail.created_at || new Date().toISOString(),
      end_date: existing.end_date || selectedEmail.end_date || null,
      user_id: existing.user_id || selectedEmail.user_id || currentUser?.user_id || null
    });
    setIsSavingEmailEdit(false);

    if (result?.success) {
      setSelectedEmail(null);
      setIsEditingEmail(false);
    }
  };

  const handleStartEditFile = () => {
    if (!selectedFile) return;
    setFileEditForm({
      title: selectedFile.title || '',
      description: selectedFile.description || ''
    });
    setIsEditingFile(true);
  };

  const handleSaveFile = async () => {
    if (!selectedFile) return;
    const title = fileEditForm.title.trim();
    if (!title) {
      setFileEditError('Filnavn må fylles ut.');
      return;
    }

    const activityId = getRecordId(selectedFile);
    const existing = activities.find(activity => getRecordId(activity) === activityId);
    if (!activityId || !existing) {
      setFileEditError('Fant ikke filmetadataen som skulle oppdateres.');
      return;
    }

    setIsSavingFile(true);
    const result = await updateActivity(activityId, {
      title,
      description: fileEditForm.description,
      type: 'Fil',
      status: 'Gjennomført',
      company_id: existing.company_id || selectedFile.company_id || company.company_id,
      deal_id: existing.deal_id || selectedFile.deal_id || selectedDealId || null,
      date: existing.date || selectedFile.date || selectedFile.created_at || new Date().toISOString(),
      end_date: existing.end_date || selectedFile.end_date || null,
      user_id: existing.user_id || selectedFile.user_id || currentUser?.user_id || null
    });
    setIsSavingFile(false);

    if (!result?.success) {
      setFileEditError(result?.error?.message || 'Kunne ikke oppdatere filmetadataen.');
      return;
    }

    setSelectedFile({
      ...selectedFile,
      ...result.data,
      id: result.data.activity_id || result.data.id || activityId,
      activity_id: result.data.activity_id || result.data.id || activityId,
      title,
      description: fileEditForm.description,
      type: 'Fil',
      status: 'Gjennomført'
    });
    setIsEditingFile(false);
  };

  const handleDeleteFile = async () => {
    if (!selectedFile) return;

    const activityId = getRecordId(selectedFile);
    const existing = activities.find(activity => getRecordId(activity) === activityId);
    if (!activityId || !existing) return;

    setIsSavingFile(true);
    const result = await updateActivity(activityId, {
      title: existing.title || selectedFile.title || 'Fil',
      description: existing.description || selectedFile.description || '',
      type: 'Fil',
      status: 'Slettet',
      company_id: existing.company_id || selectedFile.company_id || company.company_id,
      deal_id: existing.deal_id || selectedFile.deal_id || selectedDealId || null,
      date: existing.date || selectedFile.date || selectedFile.created_at || new Date().toISOString(),
      end_date: existing.end_date || selectedFile.end_date || null,
      user_id: existing.user_id || selectedFile.user_id || currentUser?.user_id || null
    });
    setIsSavingFile(false);

    if (result?.success) {
      setSelectedFile(null);
      setIsEditingFile(false);
      setFileEditForm({ title: '', description: '' });
    }
  };

  const handleStartEditDocument = () => {
    if (!selectedDocument) return;
    setDocumentForm({
      title: selectedDocument.name || selectedDocument.title || '',
      description: selectedDocument.description || ''
    });
    setIsEditingDocument(true);
  };

  const handleSaveDocument = async () => {
    if (!selectedDocument || !documentForm.title.trim()) return;

    const activityId = getRecordId(selectedDocument);
    const existing = activities.find(activity => getRecordId(activity) === activityId);
    if (!activityId || !existing) return;

    setIsSavingDocument(true);
    const result = await updateActivity(activityId, {
      title: documentForm.title.trim(),
      description: documentForm.description,
      type: 'Dokument',
      status: 'Gjennomført',
      company_id: existing.company_id || selectedDocument.company_id || company.company_id,
      deal_id: existing.deal_id || selectedDocument.deal_id || selectedDealId || null,
      date: existing.date || selectedDocument.date || selectedDocument.created_at || new Date().toISOString(),
      end_date: existing.end_date || selectedDocument.end_date || null,
      user_id: existing.user_id || selectedDocument.user_id || currentUser?.user_id || null
    });
    setIsSavingDocument(false);

    if (result?.success) {
      setSelectedDocument({
        ...selectedDocument,
        ...result.data,
        id: result.data.activity_id || result.data.id || activityId,
        activity_id: result.data.activity_id || result.data.id || activityId,
        name: result.data.title,
        title: result.data.title,
        description: result.data.description,
        type: 'Dokument',
        status: 'Gjennomført'
      });
      setIsEditingDocument(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!selectedDocument) return;
    if (!window.confirm('Er du sikker på at du vil slette dette dokumentet?')) return;

    const activityId = getRecordId(selectedDocument);
    const existing = activities.find(activity => getRecordId(activity) === activityId);
    if (!activityId || !existing) return;

    setIsSavingDocument(true);
    const result = await updateActivity(activityId, {
      title: existing.title || selectedDocument.title || selectedDocument.name || 'Dokument',
      description: existing.description || selectedDocument.description || '',
      type: 'Dokument',
      status: 'Slettet',
      company_id: existing.company_id || selectedDocument.company_id || company.company_id,
      deal_id: existing.deal_id || selectedDocument.deal_id || selectedDealId || null,
      date: existing.date || selectedDocument.date || selectedDocument.created_at || new Date().toISOString(),
      end_date: existing.end_date || selectedDocument.end_date || null,
      user_id: existing.user_id || selectedDocument.user_id || currentUser?.user_id || null
    });
    setIsSavingDocument(false);

    if (result?.success) setSelectedDocument(null);
  };

  const handleSaveTag = () => {
    if (!newTagInput.trim()) return;
    const tags = company.tags || [];
    if (!tags.some(tag => (tag.text || tag) === newTagInput.trim())) {
      updateCompany(company.company_id, {
        tags: [...tags, { text: newTagInput.trim(), color: tagColor }]
      });
    }
    setNewTagInput('');
    setTagColor('blue');
    setIsAddingTag(false);
  };

  const handleRemoveTag = index => {
    const tags = [...(company.tags || [])];
    tags.splice(index, 1);
    updateCompany(company.company_id, { tags });
  };

  const EmptyState = ({ icon, title, text }) => (
    <div className="flex flex-col items-center justify-center py-16 text-slate-400 animate-in fade-in">
      <SafeIcon icon={icon} className="w-12 h-12 mb-4 opacity-50" />
      <h4 className="font-bold text-slate-700 mb-1">{title}</h4>
      <p className="font-medium text-sm">{text}</p>
    </div>
  );

  const DetailModal = ({ editing, title, editForm, setEditForm, error, onEdit, onSave, onDelete, onClose, saving, children }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" onClick={event => event.stopPropagation()}>
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-900">{title}</h2>
        </div>
        {editing ? (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
                {title === 'Fil' ? 'Filnavn' : title === 'E-post' ? 'Emne' : 'Dokumentnavn'}
              </label>
              <input type="text" value={editForm.title} onChange={event => setEditForm(current => ({ ...current, title: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">{title === 'Fil' ? 'Beskrivelse' : 'Innhold'}</label>
              <textarea value={editForm.description} onChange={event => setEditForm(current => ({ ...current, description: event.target.value }))} className="mt-1 min-h-[120px] w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500" />
            </div>
            {error && <p className="text-sm font-medium text-rose-600">{error}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-bold rounded-lg hover:bg-slate-200">Avbryt</button>
              <button type="button" onClick={onSave} disabled={saving || !editForm.title.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50">{saving ? 'Lagrer...' : 'Lagre'}</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {children}
            <div className="flex justify-between gap-2 pt-2">
              <button type="button" onClick={onDelete} disabled={saving} className="px-4 py-2 bg-rose-50 text-rose-700 text-sm font-bold rounded-lg hover:bg-rose-100 disabled:opacity-50">Slett</button>
              <div className="flex gap-2">
                <button type="button" onClick={onEdit} className="px-4 py-2 bg-blue-50 text-blue-700 text-sm font-bold rounded-lg hover:bg-blue-100">Rediger</button>
                <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-bold rounded-lg hover:bg-slate-200">Lukk</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const companyInformationTitle = company.status === 'Customer'
    ? 'Kundeinformasjon'
    : company.status === 'Lead'
      ? 'Leadinformasjon'
      : company.status === 'Prospect'
        ? 'Prospektinformasjon'
        : company.status === 'Partner'
          ? 'Partnerinformasjon'
          : 'Selskapsinformasjon';

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans pb-20">
      <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <button onClick={() => navigate('/pipeline')} className="flex items-center text-slate-400 text-xs font-bold uppercase tracking-widest hover:text-slate-700 transition-colors mb-4">
              <SafeIcon icon="<" className="mr-2" /> TILBAKE
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-xl font-bold text-slate-600">{company.name.charAt(0).toUpperCase()}</div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 leading-tight">{company.name}</h1>
                <div className="flex items-center gap-3 mt-1.5 text-sm text-slate-600 font-medium">
                  <span><SafeIcon icon="🏢" className="text-slate-400" /> {company.name}</span>
                  <span><SafeIcon icon="👤" className="text-slate-400" /> {currentUser?.name || 'Ukjent eier'}</span>
                  <span><SafeIcon icon="🕒" className="text-slate-400" /> {formatNorwegianDate(company.created_at || Date.now())}</span>
                  <span className="px-2 py-0.5 bg-slate-200 text-slate-700 text-[10px] font-bold rounded">{company.status === 'Customer' ? 'Kunde' : company.status === 'Prospect' ? 'Prospekt' : 'Lead'}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {company.status !== 'Customer' && <button onClick={() => updateCompany(company.company_id, { status: 'Customer' })} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition-colors shadow-sm">Gjør om til kunde</button>}
            <div className="relative">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="px-3 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"><SafeIcon icon="⋮" className="w-4 h-4" /></button>
              {isMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-slate-200 shadow-xl rounded-xl z-50 py-1">
                    <button onClick={() => { setShowEditModal(true); setIsMenuOpen(false); }} className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm font-medium text-slate-700">✏️ Rediger selskap</button>
                    <button onClick={() => navigate('/companies')} className="w-full text-left px-4 py-2 hover:bg-red-50 text-sm font-medium text-red-600">🗑️ Slett selskap</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="w-full lg:w-[380px] flex flex-col gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-slate-900 text-base">{companyInformationTitle}</h3>
                <button
                  onClick={() => setShowEditModal(true)}
                  className="text-blue-600 text-sm font-bold hover:underline"
                >
                  Oppdater
                </button>
              </div>
              <div className="space-y-4 text-sm">
                {companyContacts.length ? companyContacts.map(contact => {
                  const displayPhone = contact.phone || contact.mobile || contact.phone_number || '';

                  return (
                    <div key={contact.contact_id} className="pb-4 border-b border-slate-100">
                      <div className="font-bold text-slate-900 mb-2">{contact.name}</div>
                      <div className="space-y-2 text-slate-900">
                        <div><span className="text-slate-500 font-medium">Tittel: </span>{contact.title || 'Ikke oppgitt'}</div>
                        <div><span className="text-slate-500 font-medium">Mobil: </span>{displayPhone || 'Ikke oppgitt'}</div>
                        <div><span className="text-slate-500 font-medium">E-post: </span><a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">{contact.email}</a></div>
                      </div>
                    </div>
                  );
                }) : <div className="pb-4 border-b border-slate-100 text-slate-400 italic">Ingen kontakter registrert enda.</div>}
                <div className="space-y-2 pt-2">
                  <div><span className="text-slate-500 font-medium">Nettside: </span>{company.website || '-'}</div>
                  <div><span className="text-slate-500 font-medium">Kilde: </span>{company.source || 'Nettside'}</div>
                  <div><span className="text-slate-500 font-medium">Avtaleverdi: </span>{formatCurrency(totalDealSize)}</div>
                  <div><span className="text-slate-500 font-medium">Sannsynlighet: </span>{avgProbability}%</div>
                  <div><span className="text-slate-500 font-medium">Adresse: </span>{company.visiting_address || '-'}</div>
                  <div><span className="text-slate-500 font-medium">By: </span>{company.city || '-'}</div>
                  <div><span className="text-slate-500 font-medium">Land: </span>{company.country || 'Norge'}</div>
                  <div><span className="text-slate-500 font-medium">Org.nummer: </span>{company.org_number || '-'}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col overflow-hidden">
            <div className="flex border-b border-slate-200 px-2 bg-white overflow-x-auto no-scrollbar">
              {['Tidslinje', 'Aktiviteter', 'Notater', 'Filer', 'Dokumenter', 'E-post'].map(tab => <button key={tab} className={`px-5 py-4 text-sm font-bold border-b-2 whitespace-nowrap ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-600 hover:text-slate-900'}`} onClick={() => setActiveTab(tab)}>{tab} {tab === 'Dokumenter' && <span className="text-slate-300 text-[10px]">⬆️</span>}</button>)}
            </div>
            <div className="p-8 flex-1 bg-white">
              {activeTab === 'Tidslinje' && <div><div className="flex justify-between items-center mb-8"><h3 className="font-bold text-slate-900 text-lg">Historikk</h3><button onClick={() => setShowActivityModal(true)} className="bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold">➕ Ny Aktivitet</button></div><div className="space-y-6">{companyActivities.length ? companyActivities.map((activity, index) => <div key={activity.activity_id || index} className="border border-slate-200 rounded-xl p-5 bg-white"><div className="flex justify-between items-start mb-2"><h4 className="text-sm font-bold text-slate-900">{activity.title}</h4><span className="text-xs text-slate-400">{formatNorwegianDate(activity.date || activity.created_at)}</span></div><p className="text-sm text-slate-700">{currentUser?.name || 'System'} oppdaterte denne leaden</p>{activity.description && <p className="mt-3 text-sm text-slate-600">{activity.description}</p>}</div>) : <EmptyState icon="👤" title="Lead Opprettet" text="Lead ble opprettet i systemet." />}</div></div>}
              {activeTab === 'Aktiviteter' && <div><div className="flex justify-between items-center mb-8"><h3 className="font-bold text-slate-900 text-lg">Aktiviteter</h3><button onClick={() => setShowActivityModal(true)} className="bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold">➕ Ny Aktivitet</button></div>{visibleCompanyActivities.length ? <div className="space-y-4">{visibleCompanyActivities.map((activity, index) => <div key={activity.activity_id || activity.id || index} className="border border-slate-200 rounded-xl p-5 bg-white"><div className="flex justify-between items-start gap-4 mb-2"><h4 className="text-sm font-bold text-slate-900">{activity.title || 'Aktivitet'}</h4><span className="text-xs text-slate-400 whitespace-nowrap">{formatNorwegianDate(activity.date || activity.created_at)}</span></div><div className="flex flex-wrap gap-2 text-xs text-slate-500"><span>{activity.type || 'Aktivitet'}</span><span>•</span><span>{activity.status || 'Ukjent status'}</span></div>{activity.description && <p className="mt-3 text-sm text-slate-600">{activity.description}</p>}</div>)}</div> : <p className="text-sm text-slate-500">Ingen aktiviteter registrert.</p>}</div>}
              {activeTab === 'Notater' && <div><div className="border border-slate-300 rounded-xl p-4 bg-slate-50 mb-6"><textarea value={newNote} onChange={event => setNewNote(event.target.value)} placeholder="Skriv et nytt notat her..." className="w-full bg-white border border-slate-200 rounded-lg p-3 text-sm min-h-[100px] resize-none" /><div className="flex justify-end mt-3"><button onClick={handleSaveNote} disabled={!newNote.trim()} className="px-5 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg disabled:opacity-50">Lagre Notat</button></div></div><div className="space-y-4">{companyNotes.filter(note => (note.description || note.title || '').trim()).length ? companyNotes.filter(note => (note.description || note.title || '').trim()).map((note, index) => { const noteContent = note.description || note.title; const noteUser = note.user_name || note.user || note.created_by_name || (note.user_id && note.user_id === currentUser?.user_id ? currentUser?.name : '') || 'System'; return <div key={note.activity_id || note.id || index} className="border border-slate-200 rounded-xl p-5 bg-white"><div className="flex justify-between items-start gap-4 mb-2"><h4 className="text-sm font-bold text-slate-900">Notat</h4><span className="text-xs text-slate-400 whitespace-nowrap">{formatNorwegianDate(note.date || note.created_at || note.createdAt)}</span></div><p className="text-xs text-slate-500 mb-3">{noteUser}</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{noteContent}</p></div>; }) : <p className="text-sm text-slate-500">Ingen notater registrert.</p>}</div></div>}
              {activeTab === 'Dokumenter' && <div><h3 className="font-bold text-slate-900 text-lg mb-6">Dokumenter & Avtaler</h3>{activityDocuments.length ? activityDocuments.map(document => <DocumentCard key={getRecordId(document)} {...document} onOpen={handleOpenDocument} />) : companyDocuments.map(document => <DocumentCard key={getRecordId(document)} {...document} onOpen={handleOpenDocument} />)}</div>}
            </div>
          </div>
        </div>
      </div>

      <BaseModal isOpen={showActivityModal} onClose={() => setShowActivityModal(false)} title="Ny Aktivitet"><ActivityForm onClose={() => setShowActivityModal(false)} activity={{ company_id: company.company_id, deal_id: selectedDealId || null }} /></BaseModal>
      <BaseModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title={isDealContext ? 'Rediger Lead' : 'Rediger Selskap'}>
        {isDealContext ? (
          selectedDeal ? (
            <DealForm deal={selectedDeal} onClose={() => setShowEditModal(false)} />
          ) : (
            <div className="p-6 text-sm text-slate-600">
              Fant ikke valgt lead. Lukk vinduet og prøv igjen.
            </div>
          )
        ) : (
          <CompanyForm company={company} onClose={() => setShowEditModal(false)} />
        )}
      </BaseModal>
      <CreateDocumentModal isOpen={showDocumentModal} onClose={() => setShowDocumentModal(false)} company={company} dealId={selectedDealId || null} />
    </div>
  );
};

export default CompanyDetails;